import type { InviteDto } from '@lagerfeuer/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { SettingsHeader } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { invitesApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { useAppStore } from '../../store/app';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { describeExpiry } from '../servers/inviteCode';

/** Liste aktiver Einladungen eines Servers (optional gefiltert auf einen Kanal) mit Löschen. */
export function InviteList({ serverId, channelId }: { serverId: string; channelId?: string }) {
  const qc = useQueryClient();
  const channels = useAppStore((s) => s.channels);
  const q = useQuery({ queryKey: qk.invites(serverId), queryFn: () => invitesApi.list(serverId) });
  const invites = (q.data ?? []).filter((i) => !i.isVanity && (!channelId || i.channelId === channelId));

  const remove = async (inv: InviteDto) => {
    try {
      await invitesApi.remove(inv.code);
      qc.setQueryData<InviteDto[]>(qk.invites(serverId), (list) => list?.filter((x) => x.code !== inv.code));
    } catch (e) {
      toast.error('Löschen fehlgeschlagen', errorMessage(e));
    }
  };

  if (q.isPending) {
    return (
      <div className="empty">
        <span className="spinner" aria-label="Lädt" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <p className="error-text" role="alert">
        {errorMessage(q.error)}
      </p>
    );
  }
  if (invites.length === 0) {
    return (
      <p className="empty" data-testid="invite-list-empty">
        Keine aktiven Einladungen.
      </p>
    );
  }
  return (
    <div className="table-wrap">
      <table className="settings-table" data-testid="invite-list">
        <thead>
          <tr>
            <th scope="col">Ersteller</th>
            <th scope="col">Code</th>
            {!channelId && <th scope="col">Kanal</th>}
            <th scope="col">Nutzungen</th>
            <th scope="col">Ablauf</th>
            <th scope="col">
              <span className="sr-only">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.code} data-testid="invite-row" data-code={i.code}>
              <td>{i.creator ? displayName(i.creator) : '–'}</td>
              <td>
                <code>{i.code}</code>
              </td>
              {!channelId && <td>{i.channelId && channels[i.channelId] ? `#${channels[i.channelId]!.name}` : '–'}</td>}
              <td>
                {i.uses}
                {i.maxUses > 0 ? ` / ${i.maxUses}` : ''}
              </td>
              <td>{describeExpiry(i.expiresAt)}</td>
              <td>
                <button className="icon-btn" aria-label={`Einladung ${i.code} löschen`} onClick={() => void remove(i)} data-testid="invite-delete">
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Tab „Einladungen". */
export function InvitesTab({ serverId }: { serverId: string }) {
  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Einladungen">Alle aktiven Einladungslinks dieses Servers. Gelöschte Links funktionieren sofort nicht mehr.</SettingsHeader>
      <InviteList serverId={serverId} />
    </div>
  );
}
